import useAppToast from '@/hooks/useAppToast';
import { useClinicUserSsoLinkRetry } from '@/hooks/admin';
import { useAuth } from '@/shared/providers/AuthContext';
import { useTheme } from '@/shared/providers/theme';
import { ClinicUser } from '@/types/admin-clinic-detail';
import { LoginCredentials } from '@/types/auth';
import { getApiErrorMessage } from '@/utils/apiError';
import { formatPeriodDisplay } from '@/utils/dateConverter';
import { type TableProps } from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppButton, AppTag } from '../atoms';
import { CheckCircleO, EditFill, Link, Plus } from '../atoms/AppIcon';
import AppTable from '../atoms/AppTable';

import { SITE_TYPE_MEDIX, SITE_TYPE_UPSYSTEM } from '@/shared/constants/siteTypes';
import { CLINIC_USER_ADMIN_BADGE_LABEL, CLINIC_USER_ROLE_ADMIN } from '@/constants/clinicUser';

interface ClinicUserTableProps {
  data: ClinicUser[];
  clinicId?: number;
  siteType?: string;
  clinicSiteType?: string;
  clinic_number: string;
}

const ClinicUserTable = ({ data, clinicId, siteType, clinicSiteType, clinic_number }: ClinicUserTableProps) => {
  const navigate = useNavigate();
  const toast = useAppToast();
  const { login } = useAuth();
  const { colors } = useTheme();

  // 「SSO連携」 retry (BD "SSO Clinic admin" mục 3.0). `clinicId` doubles as the query key this
  // invalidates on success — the hook is a no-op until the clinic detail page has one.
  const ssoLinkRetryMutation = useClinicUserSsoLinkRetry(siteType ?? null, String(clinicId ?? ''));
  const [retryingSsoLinkId, setRetryingSsoLinkId] = useState<number | null>(null);

  const handleRetrySsoLink = (record: ClinicUser) => {
    setRetryingSsoLinkId(record.id);
    ssoLinkRetryMutation.mutate(record.id, {
      // THE SERVER'S MESSAGE, NOT A FIXED ONE. This endpoint has two success shapes — both legs
      // written, or P1 written while 開示 has not received the clinic yet — and it says which in
      // `message`, with `p3_site_count` to branch on. Overwriting that with a flat
      // 「SSO連携しました。」 is how a half-done link came to announce itself as finished while the
      // badge stayed dark and the button stayed put (QC, clinic 123656, 03/09/2026). Three signals,
      // two of them wrong.
      onSuccess: (response) => {
        const message = response.message || 'SSO連携しました。';
        if (response.p3_site_count === 0) {
          toast.warning(message);
          return;
        }
        toast.success(message);
      },
      onError: (error: unknown) => {
        toast.error(
          getApiErrorMessage(error, 'SSO連携に失敗しました。時間をおいて再度お試しください。')
        );
      },
      onSettled: () => {
        setRetryingSsoLinkId(null);
      },
    });
  };

  const handleNavigate = () => {
    navigate(`/${siteType}/admin/clinic_users/create?clinic_id=${clinicId}`);
  };

  const isShowButton = clinicSiteType === SITE_TYPE_MEDIX || clinicSiteType === SITE_TYPE_UPSYSTEM;

  const handleNavigateEdit = (id: number) => {
    navigate(`/${siteType}/admin/clinic_users/${id}/edit`);
  };

  const handleNavigateLogin = async (data: LoginCredentials) => {
    try {
      const result = await login(data);
      if (result.success) {
        navigate(`/${clinicSiteType || siteType}`);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'ログイン失敗'));
    }
  };

  const columns: TableProps<ClinicUser>['columns'] = [
    {
      title: 'ID',
      dataIndex: 'order',
      key: 'order',
      width: '10%',
      render: (_, _record, index) => <p>{index + 1}</p>,
    },
    {
      title: '名前',
      dataIndex: 'name',
      key: 'name',
      width: '200px',
      render: (_, record) => (
        <div className="flex items-center gap-2">
          <span>{record.name}</span>
          {record.role === CLINIC_USER_ROLE_ADMIN && (
            <AppTag color="red" variant="solid" className="!mr-0">
              {CLINIC_USER_ADMIN_BADGE_LABEL}
            </AppTag>
          )}
          {/*
            BD "SSO Clinic admin" mục 2.0: 「ở bên cạnh tên user hiển thị thêm label SSO連携済み」.
            Cùng hình dạng/mã màu với AdminUserListTable — đọc từ mockup BD bằng pixel, không ước
            lượng: viên thuốc bo tròn hẳn, nền #E1F5EE, viền #5DCAA5, chữ #256E56.
          */}
          {record.sso_link_status === 'linked' ? (
            <span
              data-testid={`clinic-sso-linked-badge-${record.id}`}
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-[#5DCAA5] bg-[#E1F5EE] px-2 py-0.5 text-xs font-medium text-[#256E56]"
            >
              <CheckCircleO />
              SSO連携済み
            </span>
          ) : null}
          {/*
            'pending' = link P1 xong, chân 開示 chưa. Nhãn chứ không phải nút: chân 開示 chỉ đặt
            được khi batch P2→P3 đã mang phòng khám sang, nên KHÔNG có thao tác nào cho người vận
            hành ở trạng thái này — bày nút ra là mời họ bấm một thứ không chữa được gì (đúng lỗi
            QC báo với clinic 123656). Màu trung tính, không phải xanh thành công cũng không phải
            đỏ lỗi: đây là đang chờ, không phải hỏng.
          */}
          {record.sso_link_status === 'pending' ? (
            <span
              data-testid={`clinic-sso-pending-badge-${record.id}`}
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-[#D9D9D9] bg-[#F5F5F5] px-2 py-0.5 text-xs font-medium text-[#595959]"
              title="開示システムへの連携は、この治療院のデータが連携された後に自動的に完了します。"
            >
              開示連携待ち
            </span>
          ) : null}
        </div>
      ),
    },
    {
      title: '有効期限',
      dataIndex: 'date',
      key: 'date',
      render: (_, record) => (
        <p>{formatPeriodDisplay(record.available_from, record.available_to)}</p>
      ),
      width: '200px',
    },
    {
      title: '操作',
      key: 'action',
      width: '80px',
      render: (_, record) => (
        <div className="flex gap-2">
          <AppButton
            icon={<EditFill />}
            type="primary"
            onClick={() => handleNavigateEdit(record.id)}
          >
            編集
          </AppButton>
          {/*
            BD "SSO Clinic admin" mục 3.0: 「Khi việc đồng bộ tài khoản SSO lên Cognito thất bại…
            hiển thị button liên kết SSO để user tự retry」. Hiện đúng khi status = 'unlinked' —
            管理者権限 nhưng lần lưu vừa rồi (tự động, không cần bấm gì) chưa ghi được link.
            'none' (一般権限) và 'linked' (đã xong) đều không có gì để thử lại.
          */}
          {record.sso_link_status === 'unlinked' ? (
            <AppButton
              className="sso-link-button"
              icon={<Link />}
              onClick={() => handleRetrySsoLink(record)}
              loading={retryingSsoLinkId === record.id}
              disabled={retryingSsoLinkId !== null && retryingSsoLinkId !== record.id}
              data-testid={`clinic-sso-link-retry-${record.id}`}
            >
              SSO連携
            </AppButton>
          ) : null}
          {isShowButton && (
            <AppButton
              type="primary"
              style={{
                backgroundColor: colors.secondary,
                ['--action-bg-hover' as string]: colors.secondaryHover,
              }}
              className="hover:!bg-[var(--action-bg-hover)]"
              onClick={() =>
                handleNavigateLogin({
                  login_id: record.login_id,
                  password: record.password,
                  site_type: clinicSiteType || siteType,
                  clinic_number: clinic_number,
                  fromAdminClinicUser: true,
                })
              }
            >
              ログイン
            </AppButton>
          )}
        </div>
      ),
    },
  ];

  return (
    <section>
      <div className="flex items-center gap-10 mb-4">
        <h3 className="text-xl">治療院ユーザー</h3>
        <AppButton icon={<Plus />} type="primary" onClick={handleNavigate}>
          新規
        </AppButton>
      </div>
      <AppTable isAdminSite columns={columns} dataSource={data} rowKey={record => record.id} />
    </section>
  );
};

export default ClinicUserTable;
